import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API } from '../App';
import { useT } from '../i18n';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { Separator } from './ui/separator';
import ConfirmDialog from './ConfirmDialog';
import XpnsrPageHeader from './XpnsrPageHeader';
import XpnsrEmpty from './XpnsrEmpty';
import { Plus, Edit, Trash2, Copy, Search as SearchIcon, Network, Archive } from 'lucide-react';

// Template values the client must replace by hand (plan stage 8): REPLACE
// placeholders and an unfilled tracker host.
function manualParams(url) {
  const text = String(url || '');
  const out = [];
  if (text.includes('YOUR-TRACKER-DOMAIN')) out.push('YOUR-TRACKER-DOMAIN');
  const query = text.split('?')[1] || '';
  query.split('&').forEach((pair) => {
    const [key, value = ''] = pair.split('=');
    if (key && /^(\[REPLACE\]|REPLACE|%5BREPLACE%5D)$/i.test(value)) out.push(key);
  });
  return out;
}

const ALWAYS_LEAD = /[?&]status=lead(&|$)/;

// Same address with status=sale / status=reject instead of the fixed status=lead.
function perEventUrl(url, status) {
  return String(url || '').replace(/([?&]status=)lead(?=&|$)/, `$1${status}`);
}

const EMPTY_FORM = {
  name: '',
  vertical: '',
  template_slug: '',
  postback_url_template: '',
  offer_params: [],
  status_map_rows: [],
  notes: '',
};

export default function AffiliateNetworks() {
  const { t } = useT();
  const [networks, setNetworks] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [presetSearch, setPresetSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchNetworks();
    fetchTemplates();
  }, []);

  useEffect(() => {
    axios
      .get(API + '/analytics/affiliate-networks')
      .then((r) => {
        const map = {};
        (r.data.rows || []).forEach((row) => {
          if (row.network_id) map[row.network_id] = row;
        });
        setStats(map);
      })
      .catch(() => {});
  }, []);

  const fetchNetworks = async () => {
    try {
      setLoading(true);
      const res = await axios.get(API + '/affiliate-networks');
      setNetworks(res.data);
    } catch (err) {
      console.error('Error loading affiliate networks:', err);
      toast.error(t('Error loading affiliate networks'));
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(API + '/affiliate-networks/templates');
      setTemplates(res.data.templates || []);
    } catch (err) {
      console.error('Error loading network templates:', err);
    }
  };

  const [tplInstruction, setTplInstruction] = useState('');
  const resetForm = () => {
    setForm(EMPTY_FORM);
    setTplInstruction('');
  };

  const applyTemplate = (slug) => {
    const tpl = templates.find((x) => x.slug === slug);
    if (!tpl) return;
    setTplInstruction(tpl.instruction || '');
    setForm((prev) => ({
      ...prev,
      template_slug: slug,
      name: prev.name || tpl.name,
      vertical: tpl.vertical || prev.vertical,
      postback_url_template: tpl.url_template || prev.postback_url_template,
      offer_params:
        Array.isArray(tpl.offer_params) && tpl.offer_params.length
          ? tpl.offer_params.map((p) => ({
              key: p.key || '',
              placeholder: p.placeholder || '',
              alias: p.alias || '',
            }))
          : prev.offer_params,
      status_map_rows:
        tpl.status_mapping && Object.keys(tpl.status_mapping).length
          ? Object.entries(tpl.status_mapping).map(([raw, bucket]) => ({ raw, bucket }))
          : prev.status_map_rows,
    }));
  };

  const addParam = () => {
    setForm((prev) => ({
      ...prev,
      offer_params: [...prev.offer_params, { key: '', placeholder: '', alias: '' }],
    }));
  };

  const updateParam = (idx, field, value) => {
    setForm((prev) => {
      const next = prev.offer_params.map((p, i) =>
        i === idx ? { ...p, [field]: value } : p
      );
      return { ...prev, offer_params: next };
    });
  };

  const removeParam = (idx) => {
    setForm((prev) => ({
      ...prev,
      offer_params: prev.offer_params.filter((unused, i) => i !== idx),
    }));
  };

  const addStatusRow = () => {
    setForm((prev) => ({
      ...prev,
      status_map_rows: [...prev.status_map_rows, { raw: '', bucket: 'lead' }],
    }));
  };

  const updateStatusRow = (idx, field, value) => {
    setForm((prev) => ({
      ...prev,
      status_map_rows: prev.status_map_rows.map((r, i) =>
        i === idx ? { ...r, [field]: value } : r
      ),
    }));
  };

  const removeStatusRow = (idx) => {
    setForm((prev) => ({
      ...prev,
      status_map_rows: prev.status_map_rows.filter((unused, i) => i !== idx),
    }));
  };

  const payloadFromForm = () => ({
    name: form.name,
    vertical: form.vertical || null,
    template_slug: form.template_slug || null,
    postback_url_template: form.postback_url_template || null,
    offer_params: (form.offer_params || []).filter((p) => (p.key || '').trim()),
    status_mapping: (form.status_map_rows || []).reduce((acc, r) => {
      const rawKey = (r.raw || '').trim();
      if (rawKey) acc[rawKey] = r.bucket || 'lead';
      return acc;
    }, {}),
    notes: form.notes || null,
  });

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error(t('Name is required'));
      return;
    }
    try {
      const res = await axios.post(API + '/affiliate-networks', payloadFromForm());
      setNetworks((prev) => [res.data, ...prev]);
      setCreateOpen(false);
      resetForm();
      toast.success(t('Affiliate network created'));
    } catch (err) {
      console.error('Error creating network:', err);
      toast.error(t('Error creating affiliate network'));
    }
  };

  const openEdit = (net) => {
    setEditing(net);
    setForm({
      name: net.name || '',
      vertical: net.vertical || '',
      template_slug: net.template_slug || '',
      postback_url_template: net.postback_url_template || '',
      offer_params: net.offer_params || [],
      status_map_rows: Object.entries(net.status_mapping || {}).map(([raw, bucket]) => ({ raw, bucket })),
      notes: net.notes || '',
    });
    setEditOpen(true);
  };

  const handleUpdate = async () => {
    if (!editing) return;
    try {
      const res = await axios.put(
        API + '/affiliate-networks/' + editing.id,
        payloadFromForm()
      );
      setNetworks((prev) => prev.map((n) => (n.id === editing.id ? res.data : n)));
      setEditOpen(false);
      setEditing(null);
      resetForm();
      toast.success(t('Affiliate network updated'));
    } catch (err) {
      console.error('Error updating network:', err);
      toast.error(t('Error updating affiliate network'));
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(API + '/affiliate-networks/' + id);
      setNetworks((prev) => prev.filter((n) => n.id !== id));
      setDeleteTarget(null);
      toast.success(t('Affiliate network archived'));
    } catch (err) {
      if (err.response && err.response.status === 400) {
        toast.error(t('Cannot delete: network is linked to offers'));
      } else {
        toast.error(t('Error deleting affiliate network'));
      }
    }
  };

  const copyText = (text, label) => {
    try {
      navigator.clipboard.writeText(text);
      toast.success(label || t('Copied'));
    } catch (unused) {
      toast.error(t('Copy failed'));
    }
  };

  const inboundUrl =
    API + '/postback?subid={click_id}&status={status}&payout={payout}';

  const filtered = networks.filter((n) =>
    (n.name || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const renderForm = () => (
    <div className="space-y-6">
      <div className="space-y-2">
        <Label>{t('Quick start from preset')}</Label>
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            value={presetSearch}
            onChange={(e) => setPresetSearch(e.target.value)}
            placeholder={t('Search presets')}
            className="pl-9 mb-2"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 max-h-48 overflow-y-auto pr-1">
          {templates
            .filter((tpl) => {
              const q = presetSearch.trim().toLowerCase();
              if (!q) return true;
              return (
                (tpl.name || '').toLowerCase().includes(q) ||
                (tpl.vertical || '').toLowerCase().includes(q)
              );
            })
            .map((tpl) => (
            <button
              key={tpl.slug}
              type="button"
              onClick={() => applyTemplate(tpl.slug)}
              className={
                'flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ' +
                'transition-colors text-left ' +
                (form.template_slug === tpl.slug
                  ? 'border-primary bg-primary/10 text-primary font-medium'
                  : 'border-border hover:border-primary/40 text-muted-foreground ' +
                    'hover:text-foreground')
              }
            >
              <Network className="w-4 h-4 shrink-0" />
              <span className="truncate">{tpl.name}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t('Name')}</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            placeholder={t('Affiliate network name')}
          />
        </div>
        <div className="space-y-2">
          <Label>{t('Vertical')}</Label>
          <Input
            value={form.vertical}
            onChange={(e) => setForm((prev) => ({ ...prev, vertical: e.target.value }))}
            placeholder="iGaming / Nutra / Dating / CPA"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>{t('Inbound postback URL template')}</Label>
        {tplInstruction && (
          <p className="text-xs text-muted-foreground whitespace-pre-line rounded-md border border-border bg-surface-2 p-2">
            {tplInstruction}
          </p>
        )}
        <Input
          value={form.postback_url_template}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, postback_url_template: e.target.value }))
          }
          placeholder="https://network.com/postback?click_id={click_id}&status={status}"
        />
        {manualParams(form.postback_url_template).length > 0 ? (
          <p className="text-xs text-amber-700 dark:text-amber-400">
            {t('Fill in by hand before copying: {params}. Put the network macro for this value there (see the network postback documentation).', {
              params: manualParams(form.postback_url_template).join(', '),
            })}
          </p>
        ) : null}
        {ALWAYS_LEAD.test(form.postback_url_template || '') ? (
          <p className="text-xs text-muted-foreground">
            {t('This address always reports a lead. If the network also reports sales or rejects, add one postback per event in the network, with status=sale or status=reject instead of status=lead.')}
          </p>
        ) : null}
        <p className="text-xs text-muted-foreground">
          {t('Inbound URL for the partner network to call when it reports lead, sale, reject, or another status. Copy this into the partner dashboard; XPNSR does not call this URL as an outgoing campaign postback.')}
        </p>
      </div>

      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>{t('Offer parameters')}</Label>
          <Button variant="outline" size="sm" onClick={addParam}>
            <Plus className="w-4 h-4 mr-1" />
            {t('Add parameter')}
          </Button>
        </div>
        {form.offer_params.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('No parameters added')}</p>
        ) : (
          <div className="space-y-2">
            {form.offer_params.map((p, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                <Input
                  value={p.key || ''}
                  onChange={(e) => updateParam(idx, 'key', e.target.value)}
                  placeholder={t('Key')}
                />
                <Input
                  value={p.placeholder || ''}
                  onChange={(e) => updateParam(idx, 'placeholder', e.target.value)}
                  placeholder={t('Placeholder')}
                />
                <Input
                  value={p.alias || ''}
                  onChange={(e) => updateParam(idx, 'alias', e.target.value)}
                  placeholder={t('Alias')}
                />
                <Button variant="ghost" size="sm" aria-label={t('Remove parameter')} title={t('Remove parameter')} onClick={() => removeParam(idx)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <Separator />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label>{t('Status mapping')}</Label>
          <Button variant="outline" size="sm" onClick={addStatusRow}>
            <Plus className="w-4 h-4 mr-1" />
            {t('Add status')}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {t('Map the raw status this network sends to a canonical bucket. Applied to inbound postbacks when the campaign status map does not match.')}
        </p>
        {form.status_map_rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t('No status mappings added')}</p>
        ) : (
          <div className="space-y-2">
            {form.status_map_rows.map((r, idx) => (
              <div key={idx} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <Input
                  value={r.raw || ''}
                  onChange={(e) => updateStatusRow(idx, 'raw', e.target.value)}
                  placeholder={t('Network status (e.g. ftd)')}
                />
                <select
                  value={r.bucket || 'lead'}
                  onChange={(e) => updateStatusRow(idx, 'bucket', e.target.value)}
                  className="h-10 rounded-md border border-border bg-background px-3 text-sm"
                >
                  <option value="lead">{t('Lead')}</option>
                  <option value="sale">{t('Sale')}</option>
                  <option value="reject">{t('Reject')}</option>
                  <option value="upsell">{t('Upsell')}</option>
                  <option value="registration">{t('Registration')}</option>
                  <option value="deposit">{t('Deposit')}</option>
                  <option value="trash">{t('Trash')}</option>
                  <option value="ignore">{t('Ignore')}</option>
                </select>
                <Button variant="ghost" size="sm" aria-label={t('Remove status mapping')} title={t('Remove status mapping')} onClick={() => removeStatusRow(idx)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label>{t('Notes')}</Label>
        <Textarea
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder={t('Internal notes')}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Campaigns')}
        title={t('Affiliate networks')}
        subtitle={t('Inbound partner setup: click-ID offer parameters, status mapping, and the postback URL to paste into the partner dashboard. This does not send leads to a CRM and is not campaign S2S forwarding.')}
        actions={
          <>
          <Button asChild variant="outline" data-testid="affiliate-networks-view-archived">
            <Link to="/archive">
              <Archive className="w-4 h-4 mr-2" />
              {t('View archived')}
            </Link>
          </Button>
          <Dialog
            open={createOpen}
            onOpenChange={(open) => {
              setCreateOpen(open);
              if (!open) resetForm();
            }}
          >
            <DialogTrigger asChild>
              <Button onClick={resetForm}>
                <Plus className="w-4 h-4 mr-2" />
                {t('Create network')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('Create affiliate network')}</DialogTitle>
                <DialogDescription>
                  {t('Pick an inbound postback preset or configure the partner contract from scratch. For form-to-CRM lead delivery, create an Integration and attach it to the offer instead.')}
                </DialogDescription>
              </DialogHeader>
              {renderForm()}
              <div className="flex justify-end space-x-2 pt-4">
                <Button variant="outline" onClick={() => setCreateOpen(false)}>
                  {t('Cancel')}
                </Button>
                <Button onClick={handleCreate}>{t('Create')}</Button>
              </div>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('Search networks')}
            className="pl-9"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full">
            <Card>
              <CardContent className="py-10">
                <XpnsrEmpty
                  icon={Network}
                  title={t('No affiliate networks yet')}
                  subtitle={t('Create one from a preset like TrackBox or Adcombo.')}
                />
              </CardContent>
            </Card>
          </div>
        ) : (
          filtered.map((net) => (
            <Card key={net.id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Network className="w-5 h-5 text-primary shrink-0" />
                    <CardTitle className="truncate">{net.name}</CardTitle>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" aria-label={t('Edit affiliate network')} title={t('Edit affiliate network')} onClick={() => openEdit(net)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('Delete affiliate network')}
                      title={t('Delete affiliate network')}
                      onClick={() => setDeleteTarget(net)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {net.vertical ? (
                  <Badge variant="secondary" className="w-fit">
                    {net.vertical}
                  </Badge>
                ) : null}
              </CardHeader>
              <CardContent className="space-y-3">
                {net.postback_url_template ? (
                  <div>
                    <div className="flex items-center justify-between">
                      <Label className="text-xs text-muted-foreground">
                        {t('Inbound postback URL template')}
                      </Label>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t('Copy postback URL template')}
                        title={t('Copy postback URL template')}
                        onClick={() =>
                          copyText(net.postback_url_template, t('Postback copied'))
                        }
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <p className="text-xs font-mono bg-surface-2 border border-border p-2 rounded mt-1 break-all">
                      {net.postback_url_template}
                    </p>
                    {ALWAYS_LEAD.test(net.postback_url_template) ? (
                      <div className="mt-2 space-y-1" data-testid="per-event-postbacks">
                        <p className="text-xs text-muted-foreground">
                          {t('Ready addresses, one per event. In the network, paste each one into the postback for that event:')}
                        </p>
                        {[
                          ['lead', t('Lead')],
                          ['sale', t('Sale')],
                          ['reject', t('Reject')],
                        ].map(([ev, label]) => {
                          const url = perEventUrl(net.postback_url_template, ev);
                          return (
                            <div key={ev} className="flex items-start gap-2">
                              <span className="text-xs w-20 shrink-0 pt-1">{label}</span>
                              <p className="flex-1 text-xs font-mono bg-surface-2 border border-border p-1 rounded break-all">
                                {url}
                              </p>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label={`${t('Copy postback URL template')}: ${label}`}
                                onClick={() => copyText(url, t('Postback copied'))}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                <div>
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-muted-foreground">
                      {t('Inbound postback URL (paste in the network)')}
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t('Copy inbound postback URL')}
                      title={t('Copy inbound postback URL')}
                      onClick={() => copyText(inboundUrl, t('Inbound URL copied'))}
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-xs font-mono bg-surface-2 border border-border p-2 rounded mt-1 break-all">
                    {inboundUrl}
                  </p>
                </div>
                {stats[net.id] ? (
                  <p className="text-xs text-muted-foreground">
                    {stats[net.id].conversions +
                      ' ' +
                      t('conv.') +
                      ' \u00b7 ' +
                      Number(stats[net.id].revenue || 0).toFixed(2)}
                  </p>
                ) : null}
                {net.offer_params && net.offer_params.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    {net.offer_params.length + ' ' + t('offer parameter(s)')}
                  </p>
                ) : null}
              </CardContent>
            </Card>
          ))
        )}
      </div>

      <Dialog
        open={editOpen}
        onOpenChange={(open) => {
          setEditOpen(open);
          if (!open) {
            setEditing(null);
            resetForm();
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Edit affiliate network')}</DialogTitle>
            <DialogDescription>{t('Update partner network settings')}</DialogDescription>
          </DialogHeader>
          {renderForm()}
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleUpdate}>{t('Save')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title={t('Archive affiliate network')}
        description={t('This will move "{name}" to the archive. You can restore it or delete it permanently from the Archive page.').replace('{name}', (deleteTarget && deleteTarget.name) || '')}
        confirmLabel={t('Archive')}
        variant="default"
        onConfirm={() => handleDelete(deleteTarget && deleteTarget.id)}
      />
    </div>
  );
}
